import { useEffect, useMemo } from "react";
import { useLocation, useSearch } from "wouter";
import { Card } from "../../components/Card";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Skeleton } from "../../components/Spinner";
import { useMe } from "../../lib/useMe";
import { EksportView } from "./EksportView";
import { AbsenceAdmin } from "./AbsenceAdmin";
import { ReportView } from "./ReportView";
import { TodayView } from "./TodayView";
import { WeekView } from "./WeekView";
import styles from "./admin.module.css";

type Fane = "idag" | "uge" | "fravaer" | "rapport" | "eksport";

const FANER: { key: Fane; label: string }[] = [
  { key: "idag", label: "I dag" },
  { key: "uge", label: "Uge" },
  { key: "fravaer", label: "Fravær" },
  { key: "rapport", label: "Rapport" },
  { key: "eksport", label: "Eksport" },
];

/**
 * Chefens tidsoversigt — featurens shell-side (nav-ruten /clockin fra
 * manifestet). Tre faner på én side: delt nav-indgang og kontekst,
 * mobile-first (chefen står ofte på gulvet med telefonen).
 * Medarbejderens flade er fortsat /stemple.
 */
export function ClockinAdminPage() {
  const { me, loading: meLoading, error: meError, reload: reloadMe } = useMe();
  const search = useSearch();
  const [, navigate] = useLocation();
  const faner = useMemo(
    () =>
      me?.rolle === "bogholder"
        ? FANER.filter((fane) => fane.key === "rapport" || fane.key === "eksport")
        : FANER,
    [me?.rolle],
  );
  const faneFraUrl = new URLSearchParams(search).get("fane");
  const standardFane: Fane = me?.rolle === "bogholder" ? "rapport" : "idag";
  const urlFane = faner.some((candidate) => candidate.key === faneFraUrl)
    ? (faneFraUrl as Fane)
    : null;
  const vistFane = urlFane ?? standardFane;

  useEffect(() => {
    if (meLoading) return;
    if (urlFane === null) {
      navigate(`/clockin?fane=${standardFane}`, { replace: true });
    }
  }, [meLoading, navigate, standardFane, urlFane]);

  const selectTab = (next: Fane) => {
    navigate(`/clockin?fane=${next}`);
    document.getElementById(`clockin-tab-${next}`)?.focus();
  };

  const onTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    current: Fane,
  ) => {
    const index = faner.findIndex((candidate) => candidate.key === current);
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % faner.length;
    if (event.key === "ArrowLeft") nextIndex = (index - 1 + faner.length) % faner.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = faner.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    selectTab(faner[nextIndex]!.key);
  };

  if (meLoading) {
    return <Skeleton height="12rem" />;
  }
  if (meError !== null || me === null) {
    return (
      <Card>
        <EmptyState
          title="Kunne ikke hente din adgang"
          description="Ingen data er ændret. Kontrollér forbindelsen, og prøv igen."
          cta={<Button onClick={reloadMe}>Prøv igen</Button>}
        />
      </Card>
    );
  }

  return (
    <Card title="Tidsregistrering">
      <div className={styles.tabs} role="tablist" aria-label="Tidsregistrering">
        {faner.map((f) => (
          <button
            key={f.key}
            id={`clockin-tab-${f.key}`}
            type="button"
            role="tab"
            aria-selected={vistFane === f.key}
            aria-controls={`clockin-panel-${f.key}`}
            tabIndex={vistFane === f.key ? 0 : -1}
            className={`${styles.tab} ${vistFane === f.key ? styles.tabActive ?? "" : ""}`}
            onClick={() => selectTab(f.key)}
            onKeyDown={(event) => onTabKeyDown(event, f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div
        id={`clockin-panel-${vistFane}`}
        role="tabpanel"
        aria-labelledby={`clockin-tab-${vistFane}`}
      >
        {vistFane === "idag" && <TodayView />}
        {vistFane === "uge" && <WeekView />}
        {vistFane === "fravaer" && <AbsenceAdmin />}
        {vistFane === "rapport" && <ReportView />}
        {vistFane === "eksport" && <EksportView />}
      </div>
    </Card>
  );
}
